import styled from '@emotion/styled'

import {
  FormItem,
  GenericValidationMessages,
  Label,
  SaveSuccessMessage,
  Section,
  Select,
  Space,
  TextInput,
  ValidationMessage
} from '../../../components/core-v2'
import Icon from '../../../components/core-v2/atoms/Icon'
import IconButton from '../../../components/core-v2/molecules/iconButton'
import { Errors } from '../../tradebuilder-v2/util'
import { BankUser } from '../types'

export type UserDetailsPanelProps = {
  availableRoles: string[]
  canDelete: boolean
  canProceed: boolean
  currentUser: string
  errors: Errors
  isNew: boolean
  onChangeEmail: (name: string) => void
  onChangeFirstName: (name: string) => void
  onChangeLastName: (name: string) => void
  onChangeRole: (name: string) => void
  onChangeVerificationUser: (input: string, target: string) => void
  onDeleteUser: () => void
  onSaveUser: () => void
  saveSuccess: boolean
  selectedUser: BankUser
  serverErrors: string[]
  verificationUsername: string
}

const StyledContentSection = styled.section`
  height: calc(100vh - 119px);
  input:read-only {
    background: var(--White98);
    color: var(--White40);
    border: solid 1px var(--White90);
    cursor: default;
  }
`

export const UserDetailsPanel = ({
  availableRoles,
  canDelete,
  canProceed,
  currentUser,
  errors,
  isNew,
  onChangeEmail,
  onChangeFirstName,
  onChangeLastName,
  onChangeRole,
  onChangeVerificationUser,
  onDeleteUser,
  onSaveUser,
  saveSuccess,
  selectedUser,
  serverErrors,
  verificationUsername
}: UserDetailsPanelProps) => {
  const user = selectedUser
  return (
    <StyledContentSection>
      <Space mx={5} py={6}>
        <FormItem>
          <Label>First name</Label>
          <TextInput value={user.firstName} onChange={v => onChangeFirstName(v.target.value)} />
          <ValidationMessage errors={errors} fieldName="first_name" />
        </FormItem>
        <FormItem>
          <Label>Last name</Label>
          <TextInput value={user.lastName} onChange={v => onChangeLastName(v.target.value)} />
          <ValidationMessage errors={errors} fieldName="last_name" />
        </FormItem>
        {!isNew && (
          <FormItem>
            <Label>Username</Label>
            <TextInput readOnly value={user.userName} />
          </FormItem>
        )}
        <FormItem>
          <Label>Email</Label>
          <TextInput
            readOnly={!isNew}
            value={user.email}
            onChange={v => onChangeEmail(v.target.value)}
          />
          <ValidationMessage errors={errors} fieldName="email" />
        </FormItem>
        <FormItem>
          <Label>Role</Label>
          <Select value={user.role} onChange={v => onChangeRole(v.target.value)}>
            <option value="">Choose a role</option>
            {availableRoles.map(r => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>
          <ValidationMessage errors={errors} fieldName="role" />
        </FormItem>
        <Space isflex alignItems="center">
          <IconButton
            disabled={!canProceed}
            icon={<Icon name="save" size="default" />}
            iconPosition="left"
            operationType="progress"
            text="Save user"
            onClick={onSaveUser}
          />
          <Space ml={5}>
            <SaveSuccessMessage isVisible={saveSuccess}>
              User saved successfully!
            </SaveSuccessMessage>
          </Space>
        </Space>
        {!isNew && (
          <Section mb={5} mt={6} p={5} type="tertiary">
            <FormItem>
              <Label>Delete this user</Label>
              <TextInput
                value={verificationUsername}
                onChange={v => onChangeVerificationUser(v.target.value, currentUser)}
              />
            </FormItem>
            <IconButton
              disabled={!canDelete}
              icon={<Icon name="bin" size="default" />}
              iconPosition="left"
              operationType="negative"
              text="Delete user"
              onClick={onDeleteUser}
            />
          </Section>
        )}
        <GenericValidationMessages errorMessages={serverErrors} />
      </Space>
    </StyledContentSection>
  )
}
